import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation } from '@apollo/client';
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Divider,
  Form,
  InputNumber,
  Radio,
  Row,
  Select,
  Switch,
  message,
} from 'antd';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import ModalComponent from '../../../../../components/ModalComponent';
import { UPDATE_PINCODE_DELIVERY_SCHEDULE } from '../../../../configs/modules/graphql/Mutations';

const { Item } = Form;

const WEEKDAYS = [
  { label: 'Sunday', value: 0 },
  { label: 'Monday', value: 1 },
  { label: 'Tuesday', value: 2 },
  { label: 'Wednesday', value: 3 },
  { label: 'Thursday', value: 4 },
  { label: 'Friday', value: 5 },
  { label: 'Saturday', value: 6 },
];

const MONTHLY_WEEKS = [
  { label: '1st', value: 1 },
  { label: '2nd', value: 2 },
  { label: '3rd', value: 3 },
  { label: '4th', value: 4 },
  { label: '5th', value: 5 },
];

const getOrdinalSuffix = (i) => {
  const j = i % 10;
  const k = i % 100;
  if (j === 1 && k !== 11) return 'st';
  if (j === 2 && k !== 12) return 'nd';
  if (j === 3 && k !== 13) return 'rd';
  return 'th';
};

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => ({
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
  value: i + 1,
}));

const disabledDate = (current) => current && current < dayjs().startOf('day');

const PincodeDeliveryModal = ({
  isModalOpen,
  setIsModalOpen,
  onFinish,
  onClose,
  item,
  form,
  selectedIds = [],
  selectedPincodes = [],
}) => {
  const [frequency, setFrequency] = useState('WEEKLY');
  const [monthlyMode, setMonthlyMode] = useState('BY_DATE');

  const watchedDaysOfWeek = Form.useWatch('daysOfWeek', form) || [];
  const isAllDaysSelected = watchedDaysOfWeek.length === WEEKDAYS.length;
  const isIndeterminate =
    watchedDaysOfWeek.length > 0 && watchedDaysOfWeek.length < WEEKDAYS.length;

  const [updateSchedule, { loading: isUpdateLoading }] = useMutation(
    UPDATE_PINCODE_DELIVERY_SCHEDULE,
    {
      onCompleted: (res) => {
        message.success(
          res?.updatePincodeDeliverySchedule?.message ||
            'Schedule updated successfully',
        );
        onFinish();
      },
      onError: (err) => {
        message.error(err?.message || 'Failed to update schedule');
      },
    },
  );

  useEffect(() => {
    if (!isModalOpen) return;

    if (item) {
      const config = item.config || {};
      const recurrence = config.recurrence || {};

      const rawByWeekday = recurrence.byWeekday;
      let byWeekdayList = [{ week: 1, day: 1 }];
      if (rawByWeekday) {
        let parsed = rawByWeekday;
        if (typeof rawByWeekday === 'string') {
          try {
            parsed = JSON.parse(rawByWeekday);
          } catch (e) {
            parsed = null;
          }
        }
        if (Array.isArray(parsed) && parsed.length > 0) {
          byWeekdayList = parsed.map((w) => {
            const wk = Number(w?.week || 1);
            return {
              week: wk === -1 ? 5 : wk,
              day: Number(w?.day || 1),
            };
          });
        } else if (parsed && typeof parsed === 'object') {
          const wk = Number(parsed?.week || 1);
          byWeekdayList = [
            {
              week: wk === -1 ? 5 : wk,
              day: Number(parsed?.day || 1),
            },
          ];
        }
      }
      const isDailyRecurrence = recurrence.frequency === 'DAILY';

      const initialFreq = isDailyRecurrence
        ? 'DAILY'
        : recurrence.frequency || 'WEEKLY';

      setFrequency(initialFreq);
      setMonthlyMode(recurrence.monthlyMode || 'BY_DATE');
      form.setFieldsValue({
        frequency: initialFreq,
        isActive: item.isActive ?? true,
        interval: recurrence.interval || 1,
        anchorDate: recurrence.anchorDate
          ? dayjs(recurrence.anchorDate, 'YYYY-MM-DD')
          : dayjs(),
        daysOfWeek: recurrence.daysOfWeek || [],
        monthlyMode: recurrence.monthlyMode || 'BY_DATE',
        byMonthDays: recurrence.byMonthDays || [],
        byWeekdayList,
        customDates: (recurrence.customDates || []).map((d) =>
          dayjs(d, 'YYYY-MM-DD'),
        ),
      });
    } else {
      setFrequency('WEEKLY');
      setMonthlyMode('BY_DATE');
      form.resetFields();
      form.setFieldsValue({
        frequency: 'WEEKLY',
        isActive: true,
        interval: 1,
        anchorDate: dayjs(),
        monthlyMode: 'BY_DATE',
        byWeekdayList: [{}],
      });
    }
  }, [isModalOpen, item, form]);

  const handleValuesChange = (changedValues) => {
    if ('frequency' in changedValues) {
      setFrequency(changedValues.frequency);
    }
    if ('monthlyMode' in changedValues) {
      setMonthlyMode(changedValues.monthlyMode);
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    onClose?.();
  };

  const handleFormFinish = async (values) => {
    try {
      const isDaily = values.frequency === 'DAILY';
      let frequencyValue = values.frequency;
      let intervalValue;
      let anchorDateValue;
      let daysOfWeekValue;

      if (isDaily) {
        frequencyValue = 'DAILY';
      } else {
        if (values.frequency !== 'CUSTOM') {
          intervalValue = Number(values.interval || 1);
        }
        if (values.frequency === 'WEEKLY' || values.frequency === 'MONTHLY') {
          anchorDateValue = values.anchorDate?.format('YYYY-MM-DD');
        }
        if (values.frequency === 'WEEKLY') {
          daysOfWeekValue = values.daysOfWeek;
        }
      }
      const recurrence = {
        frequency: frequencyValue,
        interval: intervalValue,
        anchorDate: anchorDateValue,
        daysOfWeek: daysOfWeekValue,
        monthlyMode:
          values.frequency === 'MONTHLY' ? values.monthlyMode : undefined,
        byMonthDays:
          values.frequency === 'MONTHLY' && values.monthlyMode === 'BY_DATE'
            ? values.byMonthDays
            : undefined,
        byWeekday:
          values.frequency === 'MONTHLY' && values.monthlyMode === 'BY_WEEKDAY'
            ? (() => {
                const list = (values.byWeekdayList || [])
                  .filter((w) => w?.week !== undefined && w?.day !== undefined)
                  .map((w) => ({
                    week: Number(w?.week),
                    day: Number(w?.day),
                  }));
                const uniqueList = [];
                const seen = new Set();
                list.forEach((entry) => {
                  const key = `${entry.week}-${entry.day}`;
                  if (!seen.has(key)) {
                    seen.add(key);
                    uniqueList.push(entry);
                  }
                });
                return uniqueList;
              })()
            : undefined,
        customDates:
          values.frequency === 'CUSTOM'
            ? (() => {
                const rawDates = (values.customDates || [])
                  .map((d) => (d?.format ? d.format('YYYY-MM-DD') : d))
                  .filter(Boolean);
                const uniqueDates = [];
                const seen = new Set();
                rawDates.forEach((d) => {
                  if (!seen.has(d)) {
                    seen.add(d);
                    uniqueDates.push(d);
                  }
                });
                return uniqueDates;
              })()
            : undefined,
      };

      // Clean undefined keys
      Object.keys(recurrence).forEach((key) => {
        if (recurrence[key] === undefined) {
          delete recurrence[key];
        }
      });

      const config = {
        version: 1,
        recurrence,
      };

      const isBulkEdit = selectedIds && selectedIds.length > 0 && !item;

      let idsToUpdate = [];
      if (isBulkEdit) {
        idsToUpdate = selectedIds;
      } else if (item?.id) {
        idsToUpdate = [item.id];
      }
      if (idsToUpdate.length > 0) {
        const where = { ids: idsToUpdate };
        await updateSchedule({
          variables: {
            where,
            data: {
              config,
              isActive: values.isActive,
            },
          },
        });
      }
    } catch (e) {
      // errors handled by mutation onError
    }
  };

  const isBulkEdit = selectedIds && selectedIds.length > 0 && !item;

  let modalTitle = 'Add Pincode Delivery Schedule';
  if (isBulkEdit) {
    modalTitle = `Edit Pincode Delivery Schedules (${selectedIds.length})`;
  } else if (item?.id) {
    modalTitle = 'Edit Pincode Delivery Schedule';
  }

  const isSaveLoading = isUpdateLoading;

  const renderHeaderDetails = () => {
    if (isBulkEdit) {
      return (
        <Row gutter={[16, 0]} style={{ marginBottom: 20 }}>
          <Col span={24}>
            <div style={{ fontWeight: 'bold', color: '#595959' }}>
              Editing Schedules for:
            </div>
            <div
              style={{ fontSize: '16px', marginTop: 4, fontWeight: 500 }}
            >
              {selectedPincodes && selectedPincodes.length > 0
                ? selectedPincodes
                    .map((p) =>
                      p?.areaName
                        ? `${p.pincode} (${p.areaName})`
                        : p?.pincode || p,
                    )
                    .join(', ')
                : `${selectedIds.length} selected pincodes`}
            </div>
          </Col>
        </Row>
      );
    }

    if (item) {
      const p = item.pincode;
      let displayPincode = '-';
      if (p?.pincode) {
        displayPincode = p.areaName ? `${p.pincode} (${p.areaName})` : p.pincode;
      }
      return (
        <Row gutter={[16, 0]} style={{ marginBottom: 20 }}>
          <Col span={12}>
            <div style={{ fontWeight: 'bold', color: '#595959' }}>
              Pincode:
            </div>
            <div
              style={{ fontSize: '16px', marginTop: 4, fontWeight: 500 }}
            >
              {displayPincode}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ fontWeight: 'bold', color: '#595959' }}>
              Library:
            </div>
            <div
              style={{ fontSize: '16px', marginTop: 4, fontWeight: 500 }}
            >
              {item.pincode?.library?.name || 'All Libraries'}
            </div>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <ModalComponent
      open={isModalOpen}
      onCancel={handleCancel}
      onOk={form.submit}
      title={modalTitle}
      okText="Save"
      confirmLoading={isSaveLoading}
      destroyOnClose
      forceRender
      maskClosable={false}
    >
      <Divider />
      <div
        className="pincode-delivery-modal-body"
        style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}
      >
        <Form
          form={form}
          onFinish={handleFormFinish}
          onValuesChange={handleValuesChange}
          autoComplete="off"
          layout="vertical"
        >
          {renderHeaderDetails()}

          <Row gutter={[16, 0]}>
            <Col span={12}>
              <Item
                label="Frequency"
                name="frequency"
                rules={[
                  {
                    required: true,
                    message: 'Please select recurrence frequency!',
                  },
                ]}
              >
                <Select
                  options={[
                    { label: 'Daily', value: 'DAILY' },
                    { label: 'Weekly', value: 'WEEKLY' },
                    { label: 'Monthly', value: 'MONTHLY' },
                    { label: 'Custom Dates', value: 'CUSTOM' },
                  ]}
                />
              </Item>
            </Col>
            <Col span={12}>
              <Item label="Active" name="isActive" valuePropName="checked">
                <Switch />
              </Item>
            </Col>

            <Col span={24}>
              <Divider style={{ margin: '8px 0 16px 0' }} />
            </Col>

            {/* RECURRENCE TYPE SPECIFIC FIELDS */}
            {frequency === 'WEEKLY' && (
              <>
                <Col span={12}>
                  <Item
                    label="Recurrence Interval (Weeks)"
                    name="interval"
                    rules={[
                      { required: true, message: 'Please enter interval!' },
                      {
                        type: 'number',
                        min: 1,
                        message: 'Interval must be at least 1!',
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                      placeholder="Enter Interval"
                      parser={(value) =>
                        value ? value.replace(/[^\d]/g, '') : ''
                      }
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label="Anchor Date"
                    name="anchorDate"
                    rules={[
                      { required: true, message: 'Please select anchor date!' },
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%' }}
                      format="YYYY-MM-DD"
                      disabledDate={disabledDate}
                      inputReadOnly
                    />
                  </Item>
                </Col>
                <Col span={24}>
                  <Item
                    label="Days of Week"
                    required
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllDaysSelected}
                        onChange={(e) => {
                          const allValues = e.target.checked
                            ? WEEKDAYS.map((d) => d.value)
                            : [];
                          form.setFieldsValue({ daysOfWeek: allValues });
                        }}
                      >
                        Select All
                      </Checkbox>
                    </div>
                    <Item
                      name="daysOfWeek"
                      noStyle
                      rules={[
                        {
                          required: true,
                          message: 'Please select at least one day!',
                        },
                      ]}
                    >
                      <Checkbox.Group style={{ width: '100%' }}>
                        <Row gutter={[16, 8]}>
                          {WEEKDAYS.map((day) => (
                            <Col key={day.value} span={12}>
                              <Checkbox value={day.value}>{day.label}</Checkbox>
                            </Col>
                          ))}
                        </Row>
                      </Checkbox.Group>
                    </Item>
                  </Item>
                </Col>
              </>
            )}

            {frequency === 'MONTHLY' && (
              <>
                <Col span={12}>
                  <Item
                    label="Recurrence Interval (Months)"
                    name="interval"
                    rules={[
                      { required: true, message: 'Please enter interval!' },
                      {
                        type: 'number',
                        min: 1,
                        message: 'Interval must be at least 1!',
                      },
                    ]}
                  >
                    <InputNumber
                      min={1}
                      precision={0}
                      style={{ width: '100%' }}
                      parser={(value) =>
                        value ? value.replace(/[^\d]/g, '') : ''
                      }
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label="Anchor Date"
                    name="anchorDate"
                    rules={[
                      { required: true, message: 'Please select anchor date!' },
                    ]}
                  >
                    <DatePicker
                      style={{ width: '100%', cursor: 'pointer' }}
                      format="YYYY-MM-DD"
                      disabledDate={disabledDate}
                      inputReadOnly
                    />
                  </Item>
                </Col>
                <Col span={12}>
                  <Item
                    label="Monthly Mode"
                    name="monthlyMode"
                    rules={[
                      {
                        required: true,
                        message: 'Please select monthly mode!',
                      },
                    ]}
                  >
                    <Radio.Group optionType="button" buttonStyle="solid">
                      <Radio.Button value="BY_DATE">By Date</Radio.Button>
                      <Radio.Button value="BY_WEEKDAY">By Weekday</Radio.Button>
                    </Radio.Group>
                  </Item>
                </Col>

                {monthlyMode === 'BY_DATE' ? (
                  <Col span={12}>
                    <Item
                      label="Days of Month"
                      name="byMonthDays"
                      rules={[
                        {
                          required: true,
                          message: 'Please select days of month!',
                        },
                      ]}
                    >
                      <Select
                        mode="multiple"
                        showSearch={false}
                        placeholder="Select Days"
                        options={MONTH_DAYS}
                        style={{ width: '100%' }}
                      />
                    </Item>
                  </Col>
                ) : (
                  <Col span={24}>
                    <Item label="Custom Weeks">
                      <Form.List name="byWeekdayList">
                        {(fields, { add, remove }) => (
                          <>
                            {fields.map((field) => (
                              <Row
                                key={field.key}
                                gutter={16}
                                align="top"
                                style={{ marginBottom: 12 }}
                              >
                                <Col span={10}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'week']}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Please select week!',
                                      },
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select
                                      placeholder="Week Number"
                                      options={MONTHLY_WEEKS}
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={10}>
                                  <Form.Item
                                    {...field}
                                    name={[field.name, 'day']}
                                    rules={[
                                      {
                                        required: true,
                                        message: 'Please select day!',
                                      },
                                      ({ getFieldValue }) => ({
                                        validator(_, value) {
                                          if (
                                            value === undefined ||
                                            value === null
                                          )
                                            return Promise.resolve();
                                          const list =
                                            getFieldValue('byWeekdayList') ||
                                            [];
                                          const currentWeek = getFieldValue([
                                            'byWeekdayList',
                                            field.name,
                                            'week',
                                          ]);
                                          if (
                                            currentWeek === undefined ||
                                            currentWeek === null
                                          )
                                            return Promise.resolve();

                                          let duplicateCount = 0;
                                          list.forEach((entry) => {
                                            if (
                                              entry &&
                                              entry.week === currentWeek &&
                                              entry.day === value
                                            ) {
                                              duplicateCount += 1;
                                            }
                                          });
                                          if (duplicateCount > 1) {
                                            return Promise.reject(
                                              new Error('Duplicate selection!'),
                                            );
                                          }
                                          return Promise.resolve();
                                        },
                                      }),
                                    ]}
                                    style={{ marginBottom: 0 }}
                                  >
                                    <Select
                                      placeholder="Weekday"
                                      options={WEEKDAYS}
                                      style={{ width: '100%' }}
                                    />
                                  </Form.Item>
                                </Col>
                                <Col span={4}>
                                  {fields.length > 1 && (
                                    <Button
                                      type="primary"
                                      danger
                                      onClick={() => remove(field.name)}
                                      icon={<DeleteOutlined />}
                                    />
                                  )}
                                </Col>
                              </Row>
                            ))}
                            <Button
                              type="dashed"
                              onClick={() => add()}
                              style={{ width: '100%', marginTop: 4 }}
                              icon={<PlusOutlined />}
                            >
                              Add New Week Day
                            </Button>
                          </>
                        )}
                      </Form.List>
                    </Item>
                  </Col>
                )}
              </>
            )}

            {frequency === 'CUSTOM' && (
              <Col span={24}>
                <Item label="Custom Dates">
                  <Form.List name="customDates">
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => (
                          <Row
                            key={field.key}
                            gutter={16}
                            align="top"
                            style={{ marginBottom: 12 }}
                          >
                            <Col span={20}>
                              <Form.Item
                                {...field}
                                validateTrigger={['onChange', 'onBlur']}
                                rules={[
                                  {
                                    required: true,
                                    message: 'Please select a date!',
                                  },
                                  ({ getFieldValue }) => ({
                                    validator(_, value) {
                                      if (!value) return Promise.resolve();
                                      const formattedValue = dayjs(
                                        value,
                                      ).format('YYYY-MM-DD');
                                      const list =
                                        getFieldValue('customDates') || [];
                                      let duplicateCount = 0;
                                      list.forEach((d) => {
                                        if (
                                          d &&
                                          dayjs(d).format('YYYY-MM-DD') ===
                                            formattedValue
                                        ) {
                                          duplicateCount += 1;
                                        }
                                      });
                                      if (duplicateCount > 1) {
                                        return Promise.reject(
                                          new Error(
                                            'Duplicate date selection!',
                                          ),
                                        );
                                      }
                                      return Promise.resolve();
                                    },
                                  }),
                                ]}
                                style={{ marginBottom: 0 }}
                              >
                                <DatePicker
                                  style={{ width: '100%' }}
                                  format="YYYY-MM-DD"
                                  disabledDate={disabledDate}
                                  inputReadOnly
                                />
                              </Form.Item>
                            </Col>
                            <Col span={4}>
                              <Button
                                type="primary"
                                danger
                                onClick={() => remove(field.name)}
                                icon={<DeleteOutlined />}
                              />
                            </Col>
                          </Row>
                        ))}
                        <Button
                          type="dashed"
                          onClick={() => add()}
                          style={{ width: '100%', marginTop: 4 }}
                          icon={<PlusOutlined />}
                        >
                          Add Custom Date
                        </Button>
                      </>
                    )}
                  </Form.List>
                </Item>
              </Col>
            )}
          </Row>
        </Form>
      </div>
    </ModalComponent>
  );
};

export default PincodeDeliveryModal;
